import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/hooks/use-permissions";
import { useDebounce } from "@/hooks/use-debounce";
import {
  MESSAGE_THREADS_PAGE_SIZE,
  useMessageThreads,
  type MessageThread,
} from "@/hooks/use-message-threads";
import { ThreadList } from "@/components/messages/thread-list";
import { ThreadConversation } from "@/components/messages/thread-conversation";
import { EmptyState, PageHeader, Pagination } from "@/components/ui";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface MessagesSearch {
  /**
   * The conversation to open on arrival. This is the FROZEN deep-link contract
   * the new-message email nudge and every "Message tester…" affordance use
   * (`/messages?thread={threadId}`). The thread is read by this id directly —
   * no list scan — so the link resolves whatever page the thread sits on.
   * A value that cannot be a thread id is dropped rather than sent to the wire:
   * the read would only spend a request to be told 404, and an attacker-shaped
   * string would ride into a path param.
   */
  thread?: string;
  /** Zero-based page offset into the thread list. */
  offset?: number;
}

function parseThreadId(value: unknown): string | undefined {
  return typeof value === "string" && UUID_RE.test(value) ? value : undefined;
}

function parseOffset(value: unknown): number | undefined {
  const parsed =
    typeof value === "string" ? Number(value) : typeof value === "number" ? value : NaN;
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

export const Route = createFileRoute("/_authenticated/messages/")({
  component: MessagesPage,
  validateSearch: (s: Record<string, unknown>): MessagesSearch => ({
    thread: parseThreadId(s.thread),
    offset: parseOffset(s.offset),
  }),
});

/**
 * Staff Messages — every utility↔tester conversation in the org, in one place.
 * Threads are anchored to the work item they concern and are visible org-wide
 * to anyone holding `messages:read`: the conversation belongs to the utility,
 * not to whoever happened to answer. Replying additionally needs
 * `messages:create`. Customer↔tester threads never appear here — those are
 * private to their two parties.
 */
function MessagesPage() {
  const { t } = useTranslation(["messages", "common"]);
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canRead = can("messages", "read");
  const canReply = can("messages", "create");
  const search = Route.useSearch();
  const offset = search.offset ?? 0;

  // The search box is page-owned state wired into the list query, per the
  // list-page rule — the server does the matching (`q`), so a hit on page 4
  // is found. It renders inside the thread-list header because that is where
  // the mockup puts it.
  const [searchInput, setSearchInput] = useState("");
  const q = useDebounce(searchInput, 300).trim();

  // A narrowed result set renumbers the pages; holding the old offset would
  // land the reader on an empty page.
  useEffect(() => {
    if (q !== "" && offset !== 0) {
      void navigate({
        to: "/messages",
        search: search.thread ? { thread: search.thread } : {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire only when the debounced term changes
  }, [q]);

  const params = useMemo(
    () => ({ limit: MESSAGE_THREADS_PAGE_SIZE, offset, ...(q === "" ? {} : { q }) }),
    [offset, q],
  );
  const { data, isLoading, isError } = useMessageThreads(params, { enabled: canRead });

  const threads = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total_count ?? 0;
  const currentPage = Math.floor(offset / MESSAGE_THREADS_PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / MESSAGE_THREADS_PAGE_SIZE));

  const openThread = (thread: MessageThread) => {
    void navigate({
      to: "/messages",
      search: { thread: thread.id, ...(offset ? { offset } : {}) },
    });
  };

  if (!canRead) {
    return (
      <div className="p-8">
        <EmptyState
          title={t("messages:forbidden.title")}
          description={t("messages:forbidden.description")}
        />
      </div>
    );
  }

  return (
    <div className="p-8">
      <PageHeader title={t("messages:title")} subtitle={t("messages:subtitle")} />

      {isError && (
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-sm text-danger">
          {t("messages:errors.loadFailed")}
        </div>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <ThreadList
          threads={threads}
          selectedThreadId={search.thread}
          isLoading={isLoading}
          onSelect={openThread}
          search={searchInput}
          onSearchChange={setSearchInput}
          onResetSearch={() => setSearchInput("")}
        />
        {search.thread ? (
          <ThreadConversation key={search.thread} threadId={search.thread} canReply={canReply} />
        ) : (
          <EmptyState
            title={t("messages:pane.noneSelectedTitle")}
            description={t("messages:pane.noneSelectedDescription")}
          />
        )}
      </div>

      {total > MESSAGE_THREADS_PAGE_SIZE && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={total}
          pageSize={MESSAGE_THREADS_PAGE_SIZE}
          onPageChange={(page) =>
            void navigate({
              to: "/messages",
              // Paging must not close the open conversation.
              search: {
                ...(search.thread ? { thread: search.thread } : {}),
                ...(page > 1 ? { offset: (page - 1) * MESSAGE_THREADS_PAGE_SIZE } : {}),
              },
            })
          }
        />
      )}
    </div>
  );
}
